<template>
  <div class="container">
    <div class="navigation">
      <button @click="setToday">Dzisiaj</button>
      <button class="md-icon" @click="prev()">chevron_left</button>
      <button class="md-icon" @click="next()">chevron_right</button>
      <div class="heading">
        <span>{{ headingText }} </span><span class="secondary">{{ subheadingText }}</span>
      </div>
    </div>

    <div class="content" :class="{ vertical, horizontal }">
      <div class="days-column">
        <button
          class="show-more"
          v-if="isCurrentWeek"
          @click="showPastDays = !showPastDays"
        >
          <div class="md-icon">today</div>

          <button>{{ showPastDays ? "ukryj" : "pokaż" }} {{ previousDays }}</button>
        </button>
        <ItemList
          :class="{ today: day.offset === 0 }"
          v-for="day in days"
          :itemID="day.id"
          :key="day.id"
          v-show="day.offset >= 0 || showPastDays || !isCurrentWeek"
        />
      </div>
      <div class="week-column">
        <ItemList class="week-column" :itemID="itemID" />
        <button v-if="!hintsShown.usingWeeks" class="md-icon hint" @click="weekHint">
          help
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.navigation {
  display: flex;
  flex-direction: row;
  margin-left: 8px;
}
.heading {
  font-size: 1.5rem;
  padding: 8px;
}
.heading .secondary {
  opacity: var(--text-opacity-tertiary);
}

.container {
  display: flex;
  flex-direction: column;
  height: fit-content;

  margin: 16px;
  border-radius: var(--rounding-larger);

  background: #fffdf477;
  padding: var(--basic-padding);
  gap: var(--basic-gap);
  border: var(--list-border);
}
.content {
  display: flex;
  flex-direction: row;
}
.content.vertical {
  flex-direction: column;
}
.days-column {
  display: block;
  display: flex;
  flex-direction: column;
  margin: var(--basic-margin);
  gap: calc(2 * var(--basic-gap));
}
.days-column .item-list {
  margin: 0;
}
.horizontal .days-column {
  flex-direction: row;
}
.item-list.today::v-deep .list-header {
  font-weight: bold;
}

.week-column::v-deep .header .secondary {
  display: none;
}
button.show-more {
  display: flex;
  align-items: center;
  justify-content: left;
  padding: 0;
  /* background-color: var(--card-color-default); */
  border-radius: 0;
  margin: 6px 8px;
  border-bottom: var(--date-header-underline);
}
.show-more .md-icon {
  padding: var(--basic-padding);
}
.show-more button {
  padding: 4px 8px;
  background-color: var(--card-color-default);
}

button.hint {
  margin: 32px auto;
  background-color: transparent;
}
</style>

<script>
import ItemHeader from "@/components/ItemHeader.vue";
import ItemList from "@/components/ItemList.vue";
import hintsShown from "@/hints";
import moment from "moment";
import { getItem } from "@/reactive-cache";
import { toMomentJSUnit, formatDate } from "@/time-utils";
import HelpHint from "@/components/HelpHint.vue";

const isoDateFormat = "Y-MM-DD";

export default {
  props: ["vertical", "horizontal"],
  components: {
    ItemHeader,
    ItemList,
    HelpHint,
  },
  data() {
    return {
      showPastDays: false,
      hintsShown: hintsShown,
    };
  },
  computed: {
    itemID() {
      let date;
      if (this.$route.query.week === "current" || !this.$route.query.week) {
        date = moment().startOf(toMomentJSUnit("week")).format("Y-MM-DD");
      } else {
        date = this.$route.query.week;
      }
      return "week:" + date;
    },
    item() {
      return getItem(this.itemID);
    },
    weekStart() {
      return moment(this.itemID.split(":")[1]);
    },
    weekEnd() {
      return this.weekStart.clone().add(1, "week");
    },
    headingText() {
      const text = formatDate(this.itemID);
      return text[0].toUpperCase() + text.slice(1);
    },
    subheadingText() {
      return formatDate(this.itemID, false);
    },
    days() {
      const now = moment().startOf("day");
      const date = this.weekStart.clone();
      const result = [];
      while (date.isBefore(this.weekEnd)) {
        result.push({
          id: "day:" + date.format("Y-MM-DD"),
          offset: moment.duration(date.diff(now.clone())).asDays(),
        });
        date.add(1, "day");
      }
      return result;
    },
    previousDays() {
      const days = this.days.filter((day) => day.offset < 0);
      if (days.length === 0) {
        return "";
      }
      if (days.length === 1) {
        return moment(days[0].id.split(":")[1]).format("dddd");
      }
      const first = moment(days[0].id.split(":")[1]).format("ddd");
      const last = moment(days[days.length - 1].id.split(":")[1]).format("ddd");
      return `${first} - ${last}`;
    },
    isCurrentWeek() {
      return (
        moment().startOf("day").isAfter(this.weekStart) && moment().isBefore(this.weekEnd)
      );
    },
  },
  methods: {
    weekHint() {
      window.alert(
        "Nie wszystko musi być zaplanowane na konkretny dzień!\n" +
          "Wstaw do tej kolumny zadania bez sztywnego terminu.",
      );
      hintsShown.usingWeeks = true;
    },
    prev() {
      this.openWeek(-1);
    },
    next() {
      this.openWeek(1);
    },
    openWeek(offset) {
      const [unit, date] = this.item._id.split(":");
      const startMoment = moment(date).startOf(toMomentJSUnit(unit));
      const nextDate = startMoment.add(offset, toMomentJSUnit(unit));
      const newRoute = {
        name: this.$route.name,
        params: { ...this.$route.params },
        query: { ...this.$route.query, week: nextDate.format(isoDateFormat) },
      };
      this.$router.safePush(newRoute);
    },
    setToday() {
      const newRoute = {
        name: this.$route.name,
        params: { ...this.$route.params },
        query: { ...this.$route.query, week: "current" },
      };
      this.$router.safePush(newRoute);
    },
  },
};
</script>
